import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      // No credentials: the token is in the body, so this stays a "simple" cross-origin
      // request that works even when the reset link is opened on a different preview host.
      const res = await fetch(`${API}/auth/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: pw }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(typeof j.detail === "string" ? j.detail : "Could not reset password");
      }
      setDone(true);
    } catch (er) {
      toast.error(er?.message || "Network error — please try again");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 p-8 shadow-xl">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/azexpan-logo.png" alt="AzexPan" className="h-8" data-testid="reset-logo" />
        </div>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
            <h2 className="mt-4 font-heading text-xl font-bold">Password set</h2>
            <p className="mt-1 text-sm text-slate-500">You can now sign in with your new password.</p>
            <Button className="mt-6 bg-azx-blue hover:bg-blue-700" data-testid="reset-go-login" onClick={() => nav("/login")}>Go to sign in</Button>
          </div>
        ) : !token ? (
          <div className="text-center py-6"><p className="text-slate-500">This reset link is missing its token. Please use the link from your email.</p><Link to="/login" className="mt-4 inline-block text-azx-blue">Back to sign in</Link></div>
        ) : (
          <form onSubmit={submit}>
            <div className="flex items-center gap-2 mb-4"><KeyRound className="h-5 w-5 text-azx-blue" /><h2 className="font-heading text-xl font-bold">Set your password</h2></div>
            <div className="space-y-4">
              <div><Label>New password</Label><Input type="password" data-testid="reset-pw" className="mt-1.5" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
              <div><Label>Confirm password</Label><Input type="password" data-testid="reset-pw2" className="mt-1.5" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
              <Button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-azx-blue hover:bg-blue-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password"}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
